'use client';

import { useEffect, useState, useRef } from 'react';
import { sanitizeHTML } from '@/lib/utils';

interface AnchorsProps {
  data: {
    heading?: {
      text?: string;
      title?: string;
    };
    subheading?: {
      text?: string;
      title?: string;
    };
    anchors?: {
      anchor_id: {
        anchor_id: string
        label: string
      }
    }[]
  }
}

export const fields = [
  'heading.text',
  'heading.title',
  'subheading.text',
  'subheading.title',
  'anchors.anchor_id.anchor_id',
  'anchors.anchor_id.label',
] as const

export default function Anchors({ data }: AnchorsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    setMounted(true);
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    const updateHeaderHeight = () => {
      const header = document.querySelector("header");
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    const setScrollMargin = () => {
      const anchors = document.querySelectorAll('[id]');
      anchors.forEach(anchor => {
        (anchor as HTMLElement).style.scrollMarginTop = '120px';
      });
    };

    checkMobile();
    updateHeaderHeight();
    setScrollMargin();

    window.addEventListener("resize", () => {
      checkMobile();
      updateHeaderHeight();
      setScrollMargin();
    });
    
    return () => window.removeEventListener("resize", () => {
      checkMobile();
      updateHeaderHeight();
    });
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string, label: string) => {
    e.preventDefault();
    
    setSelectedSection(label);
    setIsOpen(false);
    
    const element = document.getElementById(id);
    if (element) {
      // Get header and navigation heights
      const header = document.querySelector("header");
      const navigation = document.querySelector("section#anchors");
      
      const headerOffset = header ? header.getBoundingClientRect().height : 65;
      const navigationOffset = navigation ? navigation.getBoundingClientRect().height : 50;
      
      // Calculate total offset
      const totalOffset = headerOffset + navigationOffset + 20; // Adding extra padding
      
      // Get the element's position
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      
      // Calculate the position to scroll to
      const offsetPosition = elementPosition - totalOffset;
      
      // Scroll to element with offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
		<section
			id='anchors'
			className='Anchors w-full sticky top-[60px] md:top-[80px] z-[99]'
		>
			<div className='w-full bg-neutral-50'>
				<div className='w-full'>
					<button
						onClick={() => setIsOpen(!isOpen)}
						className='w-full cursor-pointer bg-neutral-50 py-6'
					>
						<div className='px-5 md:px-0 w-full'>
							<div className='flex flex-col md:flex-row md:items-center md:justify-between w-full container mx-auto'>
								<span className='text-xl font-brandon uppercase tracking-wide relative'>
									{data?.heading?.title ? (
										<div
											dangerouslySetInnerHTML={sanitizeHTML(data.heading.title)}
										/>
									) : (
										'Table of Contents'
									)}
									<span className='md:hidden absolute right-0 top-1/2 -mt-1 border-[6px] border-transparent border-t-black'></span>
								</span>
								<div className='md:flex items-center'>
									{/* <div className='md:hidden text-lg w-fit relative pr-6 mt-2 max-md:m-auto'>
										{selectedSection ||
                      (data?.heading?.text ? (
                        <div dangerouslySetInnerHTML={sanitizeHTML(data.heading.text)} />
                      ) : (
                        'Select Section'
                      ))}
										<span className='absolute right-0 top-1/2 -mt-1 border-[6px] border-transparent border-t-black'></span>
									</div> */}
									<div className='hidden md:flex items-center text-base font-brandon'>
										{isOpen ? 'Hide sections' : 'Show sections'}
										<span
											className={`ml-2 transition-transform duration-200 ${
												isOpen ? 'rotate-180' : ''
											}`}
										>
											<svg
												width='12'
												height='8'
												viewBox='0 0 12 8'
												fill='none'
												xmlns='http://www.w3.org/2000/svg'
											>
												<path
													d='M1 1L6 6L11 1'
													stroke='currentColor'
													strokeWidth='2'
													strokeLinecap='round'
												/>
											</svg>
										</span>
									</div>
								</div>
							</div>
						</div>
					</button>

					<div
						className={`${
							isOpen ? 'block' : 'hidden'
						} w-full bg-neutral-50 transition-all duration-300`}
					>
						<div
							ref={contentRef}
							className='container mx-auto'
							style={{
								maxHeight: mounted
									? isMobile
										? 'min(80vh, 500px)'
										: 'min(50vh, 600px)'
									: '0',
								overflow: 'auto',
							}}
						>
							<div className='max-lg:mt-7 gap-4 flex flex-col md:flex-row md:flex-wrap md:justify-center pt-4 pb-8'>
								{data?.anchors?.map((anchor, index) => (
									<a
										key={index}
										href={`#${anchor.anchor_id.anchor_id}`}
										onClick={e =>
											handleSmoothScroll(
												e,
												anchor.anchor_id.anchor_id,
												anchor.anchor_id.label
											)
										}
										className='px-4 py-3 md:py-1 rounded outline outline-[0.30px] outline-offset-[-0.30px] outline-black hover:bg-primary/10 transition-colors'
									>
										<span className='text-base md:text-lg font-brandon leading-loose'>
											{index + 1}. {anchor.anchor_id.label}
										</span>
									</a>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}